import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faFileExcel,
    faTimesCircle,
    faTrophy,
    faChevronDown,
    faChevronUp,
    faSearch,
    faStar
} from '@fortawesome/free-solid-svg-icons';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import api from '../../Api';
import { useAuth } from '../../contexts/AuthContext';
import SearchableSelect from '../../components/SearchableSelect';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const ControlStats = () => {
    const { user } = useAuth();
    // Roles that can only see CATI surveys in control stats (not F2F).
    const isCatiOnlyUser = user?.role === 'resp_cati' || user?.role === 'supervisor_cati';
    // Roles that can only see F2F surveys in control stats (not CATI).
    const isF2fOnlyUser = user?.role === 'chef_terrain';
    const [controlStats, setControlStats] = useState(null);
    const [controlStatsLoading, setControlStatsLoading] = useState(false);
    const [selectedSurveyForStats, setSelectedSurveyForStats] = useState(null);
    const [availableSurveys, setAvailableSurveys] = useState([]);
    const [showResumeData, setShowResumeData] = useState(false);
    const [showRawData, setShowRawData] = useState(false);
    const [resumeSearchTerm, setResumeSearchTerm] = useState('');
    const [rawDataSearchTerm, setRawDataSearchTerm] = useState('');
    const [resumeSearchColumn, setResumeSearchColumn] = useState('all');
    const [rawDataSearchColumn, setRawDataSearchColumn] = useState('all');
    const [resumeFilteredData, setResumeFilteredData] = useState(null);
    const [rawDataFilteredData, setRawDataFilteredData] = useState(null);
    const [reasonsDistributionSearchInput, setReasonsDistributionSearchInput] = useState('');
    const [reasonsDistributionSearchTerm, setReasonsDistributionSearchTerm] = useState('');
    const [isF2fSurvey, setIsF2fSurvey] = useState(false);
    // F2F-only users always get the F2F layout, even with "Toutes les enquêtes" selected.
    const showF2fView = isF2fOnlyUser || isF2fSurvey;

    useEffect(() => {
        fetchAvailableSurveys();
    }, [user?.role]);

    useEffect(() => {
        fetchControlStats();
    }, [selectedSurveyForStats, isF2fSurvey]);

    useEffect(() => {
        // Initialize filtered data with all data when stats are loaded
        if (controlStats) {
            if (controlStats.resume_data) {
                setResumeFilteredData(controlStats.resume_data);
            }
            if (controlStats.raw_data) {
                setRawDataFilteredData(controlStats.raw_data);
            }
        }
    }, [controlStats]);

    const fetchAvailableSurveys = async () => {
        try {
            const response = await api.get('/control/surveys', { params: { per_page: 1000 } }).catch(() => ({ data: { data: [] } }));
            let surveys = response.data.data || response.data || [];
            // resp_cati & supervisor_cati: only show CATI surveys
            if (isCatiOnlyUser) {
                surveys = surveys.filter(s => s.is_f2f !== true);
            }
            // chef_terrain: only show F2F surveys
            if (isF2fOnlyUser) {
                surveys = surveys.filter(s => s.is_f2f === true);
            }
            setAvailableSurveys(surveys);
            setSelectedSurveyForStats(null);
        } catch (error) {
            console.error('Error fetching surveys:', error);
        }
    };

    const buildStatsParams = () => {
        const params = {};
        if (selectedSurveyForStats !== null && selectedSurveyForStats !== '') {
            params.survey_id = selectedSurveyForStats;
        }
        if (isCatiOnlyUser) {
            params.source = 'cati';
        } else if (showF2fView) {
            params.source = 'f2f';
        }
        return params;
    };

    const fetchControlStats = async () => {
        try {
            setControlStatsLoading(true);
            // Initial load: skip the heavy resume_data and raw_data tables (they're collapsed by default).
            const response = await api.get('/control/stats', { params: buildStatsParams() });
            if (response.data.success) {
                setControlStats(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching control statistics:', error);
        } finally {
            setControlStatsLoading(false);
        }
    };

    const [resumeLoading, setResumeLoading] = useState(false);
    const [rawLoading, setRawLoading] = useState(false);

    const fetchResumeDataIfMissing = async () => {
        if (!controlStats || controlStats.resume_data !== undefined) return;
        try {
            setResumeLoading(true);
            const response = await api.get('/control/stats', { params: { ...buildStatsParams(), include: 'resume' } });
            if (response.data.success) {
                setControlStats(prev => ({ ...prev, resume_data: response.data.data.resume_data || [] }));
            }
        } catch (error) {
            console.error('Error fetching resume data:', error);
        } finally {
            setResumeLoading(false);
        }
    };

    const fetchRawDataIfMissing = async () => {
        if (!controlStats || controlStats.raw_data !== undefined) return;
        try {
            setRawLoading(true);
            const response = await api.get('/control/stats', { params: { ...buildStatsParams(), include: 'raw' } });
            if (response.data.success) {
                setControlStats(prev => ({ ...prev, raw_data: response.data.data.raw_data || [] }));
            }
        } catch (error) {
            console.error('Error fetching raw data:', error);
        } finally {
            setRawLoading(false);
        }
    };

    const handleResumeSearch = () => {
        if (!controlStats || !controlStats.resume_data) return;
        
        if (!resumeSearchTerm.trim()) {
            setResumeFilteredData(controlStats.resume_data);
            return;
        }

        const searchLower = resumeSearchTerm.toLowerCase();
        const filtered = controlStats.resume_data.filter((row) => {
            if (resumeSearchColumn === 'all') {
                return Object.values(row).some(value => 
                    value !== null && value !== undefined && 
                    String(value).toLowerCase().includes(searchLower)
                );
            } else {
                const value = row[resumeSearchColumn];
                return value !== null && value !== undefined && 
                    String(value).toLowerCase().includes(searchLower);
            }
        });
        setResumeFilteredData(filtered);
    };

    const handleRawDataSearch = () => {
        if (!controlStats || !controlStats.raw_data) return;
        
        if (!rawDataSearchTerm.trim()) {
            setRawDataFilteredData(controlStats.raw_data);
            return;
        }

        const searchLower = rawDataSearchTerm.toLowerCase();
        const filtered = controlStats.raw_data.filter((row) => {
            if (rawDataSearchColumn === 'all') {
                return Object.values(row).some(value => 
                    value !== null && value !== undefined && 
                    String(value).toLowerCase().includes(searchLower)
                );
            } else {
                const value = row[rawDataSearchColumn];
                return value !== null && value !== undefined && 
                    String(value).toLowerCase().includes(searchLower);
            }
        });
        setRawDataFilteredData(filtered);
    };

    const renderStarRating = (rating, rawRating, totalChecks, mostCommonReason) => {
        if (rating === null || rating === undefined || isNaN(rating)) {
            return (
                <div className="flex items-center gap-1">
                    <span className="text-gray-400 text-sm">N/A</span>
                </div>
            );
        }

        // Always use rawRating if available (it's the actual 1-5 rating from database)
        // Only fall back to percentage conversion if rawRating is missing
        let numRating;
        if (rawRating !== null && rawRating !== undefined && !isNaN(rawRating) && Number(rawRating) > 0) {
            numRating = Number(rawRating);
        } else if (rating !== null && rating !== undefined && !isNaN(rating)) {
            // Convert percentage (0-100) to 1-5 scale as fallback
            numRating = (Number(rating) / 100) * 5;
        } else {
            return (
                <div className="flex items-center gap-1">
                    <span className="text-gray-400 text-sm">N/A</span>
                </div>
            );
        }

        // Ensure rating is between 0 and 5
        numRating = Math.max(0, Math.min(5, numRating));
        
        // Render 5 stars with accurate partial filling
        const stars = [];
        for (let i = 0; i < 5; i++) {
            const starValue = numRating - i;
            let fillPercentage = 0;
            
            if (starValue >= 1) {
                fillPercentage = 100; // Fully filled
            } else if (starValue > 0) {
                fillPercentage = starValue * 100; // Partially filled based on exact percentage
            }
            
            stars.push(
                <span 
                    key={i} 
                    className="relative inline-block" 
                    style={{ width: '14px', height: '14px', display: 'inline-block', verticalAlign: 'middle' }}
                >
                    {/* Background gray star - always full */}
                    <FontAwesomeIcon 
                        icon={faStar} 
                        className="text-gray-300 text-sm"
                        style={{ position: 'absolute', left: 0, top: 0, width: '14px', height: '14px' }}
                    />
                    {/* Foreground red star - clipped based on fill percentage */}
                    {fillPercentage > 0 && (
                        <div
                            className="absolute top-0 left-0 overflow-hidden"
                            style={{
                                width: `${fillPercentage}%`,
                                height: '100%'
                            }}
                        >
                            <FontAwesomeIcon 
                                icon={faStar} 
                                className="text-red-600 text-sm"
                                style={{ width: '14px', height: '14px', display: 'block' }}
                            />
                        </div>
                    )}
                </span>
            );
        }

        // Calculate the display value
        const displayValue = rawRating !== null && rawRating !== undefined && !isNaN(rawRating) 
            ? Number(rawRating).toFixed(1) 
            : (rating ? ((Number(rating) / 100) * 5).toFixed(1) : 'N/A');

        return (
            <div className="relative group" style={{ zIndex: 1 }}>
                <div className="flex items-center gap-0.5">
                    {stars}
                </div>
                
                {/* Simple tooltip on hover - just the star value */}
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block pointer-events-none whitespace-nowrap" style={{ zIndex: 10000 }}>
                    <div className="bg-gray-900 text-white text-xs rounded py-1.5 px-2 shadow-lg relative">
                        {displayValue}/5
                        {/* Arrow pointing down */}
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-px">
                            <div className="border-4 border-transparent border-t-gray-900"></div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const handleExportStats = async () => {
        try {
            const params = {};
            if (selectedSurveyForStats !== null && selectedSurveyForStats !== '') {
                params.survey_id = selectedSurveyForStats;
            }
            if (isCatiOnlyUser) {
                params.source = 'cati';
            } else if (showF2fView) {
                params.source = 'f2f';
            }

            const response = await api.get('/control/stats/export', {
                params,
                responseType: 'blob',
            });

            const contentDisposition = response.headers['content-disposition'];
            let filename = 'statistiques_controle.xlsx';
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error exporting control statistics:', error);
            alert('Erreur lors de l\'exportation. Veuillez réessayer.');
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 relative overflow-hidden">
            {/* Dotted Background */}
            <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0" style={{
                    backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
                    backgroundSize: '20px 20px'
                }}></div>
            </div>

            <div className="relative z-10 p-6">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="mb-8">
                        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3">
                            Statistiques de Contrôle
                        </h1>
                        <p className="text-gray-600">Vue d'ensemble des statistiques de contrôle qualité</p>
                    </div>

                    {/* Survey Filter and Export Button */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Filtrer par Enquête
                                </label>
                                <SearchableSelect
                                    options={availableSurveys}
                                    selectedValue={selectedSurveyForStats}
                                    onChange={(val) => {
                                        setSelectedSurveyForStats(val);
                                        if (val) {
                                            const survey = availableSurveys.find(s => s.id === val);
                                            setIsF2fSurvey(survey?.is_f2f === true);
                                        } else {
                                            setIsF2fSurvey(false);
                                        }
                                    }}
                                    placeholder="Toutes les enquêtes"
                                    searchPlaceholder="Rechercher par titre ou ID..."
                                    getOptionLabel={(s) => `${s.title || s.name}${s.control_enabled || s.enabled ? ' (Activée)' : ''}`}
                                    getOptionMeta={(s) => s.limesurvey_id ? `ID: ${s.limesurvey_id}` : null}
                                    maxHeight="250px"
                                    triggerClassName="px-4 py-2"
                                    textClassName="text-sm"
                                />
                            </div>
                            <button
                                onClick={handleExportStats}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 w-full sm:w-auto"
                                title="Exporter en Excel"
                            >
                                <FontAwesomeIcon icon={faFileExcel} />
                                <span>Exporter en Excel</span>
                            </button>
                        </div>
                    </div>

                    {/* Statistics Content */}
                    {controlStatsLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
                        </div>
                    ) : controlStats ? (
                        <div className="space-y-6">
                            {/* Points Saillants */}
                            <div className="bg-gray-50 rounded-lg p-6">
                                <h4 className="text-xl font-bold text-gray-900 mb-4">Points Saillants</h4>
                                {showF2fView ? (
                                    <>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
                                            <div className="bg-white rounded-lg p-4 shadow-sm">
                                                <p className="text-sm text-gray-600 mb-1">Enquêteurs F2F</p>
                                                <p className="text-2xl font-bold text-gray-900">{controlStats.highlights?.total_interviewers || 0}</p>
                                            </div>
                                            <div className="bg-white rounded-lg p-4 shadow-sm">
                                                <p className="text-sm text-gray-600 mb-1">Prestataires</p>
                                                <p className="text-2xl font-bold text-purple-600">{controlStats.highlights?.total_supervisors || 0}</p>
                                            </div>
                                            <div className="bg-white rounded-lg p-4 shadow-sm">
                                                <p className="text-sm text-gray-600 mb-1">Total Soumissions</p>
                                                <p className="text-2xl font-bold text-gray-900">{controlStats.highlights?.total_submissions || 0}</p>
                                            </div>
                                            <div className="bg-white rounded-lg p-4 shadow-sm">
                                                <p className="text-sm text-gray-600 mb-1">Total Validées</p>
                                                <p className="text-2xl font-bold text-green-600">{controlStats.highlights?.total_validated || 0}</p>
                                            </div>
                                            <div className="bg-white rounded-lg p-4 shadow-sm">
                                                <p className="text-sm text-gray-600 mb-1">Total Rejetées</p>
                                                <p className="text-2xl font-bold text-red-600">{controlStats.highlights?.total_rejected || 0}</p>
                                            </div>
                                        </div>

                                        {/* Best Performing Person Cards - F2F */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                            {controlStats.highlights?.best_person_by_score && (
                                                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                                                    <p className="text-sm font-semibold text-green-800 mb-2">Meilleur par Score</p>
                                                    <p className="text-lg font-bold text-green-900">
                                                        {controlStats.highlights.best_person_by_score.person_name}
                                                        <span className={`ml-2 px-1.5 py-0.5 text-xs rounded ${controlStats.highlights.best_person_by_score.person_type === 'supervisor' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                                                            {controlStats.highlights.best_person_by_score.person_type === 'supervisor' ? 'Prestataire' : 'Enquêteur'}
                                                        </span>
                                                    </p>
                                                    <p className="text-sm text-gray-600 mt-2">Ref: {controlStats.highlights.best_person_by_score.person_ref}</p>
                                                    <p className="text-sm font-semibold text-green-800 mt-1">
                                                        Note Moyenne: {Number(controlStats.highlights.best_person_by_score.average_rating).toFixed(2)}%
                                                    </p>
                                                </div>
                                            )}
                                            {controlStats.highlights?.best_person_by_rejects && (
                                                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                                                    <p className="text-sm font-semibold text-green-800 mb-2">Meilleur par Rejets</p>
                                                    <p className="text-lg font-bold text-green-900">
                                                        {controlStats.highlights.best_person_by_rejects.person_name}
                                                        <span className={`ml-2 px-1.5 py-0.5 text-xs rounded ${controlStats.highlights.best_person_by_rejects.person_type === 'supervisor' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                                                            {controlStats.highlights.best_person_by_rejects.person_type === 'supervisor' ? 'Prestataire' : 'Enquêteur'}
                                                        </span>
                                                    </p>
                                                    <p className="text-sm text-gray-600 mt-2">Ref: {controlStats.highlights.best_person_by_rejects.person_ref}</p>
                                                    <p className="text-sm font-semibold text-green-800 mt-1">
                                                        Rejets: {controlStats.highlights.best_person_by_rejects.rejections_count}
                                                    </p>
                                                </div>
                                            )}
                                        </div>

                                        {/* Worst Performing Person Cards - F2F */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {controlStats.highlights?.worst_person_by_score && (
                                                <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                                                    <p className="text-sm font-semibold text-red-800 mb-2">Pire par Score</p>
                                                    <p className="text-lg font-bold text-red-900">
                                                        {controlStats.highlights.worst_person_by_score.person_name}
                                                        <span className={`ml-2 px-1.5 py-0.5 text-xs rounded ${controlStats.highlights.worst_person_by_score.person_type === 'supervisor' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                                                            {controlStats.highlights.worst_person_by_score.person_type === 'supervisor' ? 'Prestataire' : 'Enquêteur'}
                                                        </span>
                                                    </p>
                                                    <p className="text-sm text-gray-600 mt-2">Ref: {controlStats.highlights.worst_person_by_score.person_ref}</p>
                                                    <p className="text-sm font-semibold text-red-800 mt-1">
                                                        Note Moyenne: {Number(controlStats.highlights.worst_person_by_score.average_rating).toFixed(2)}%
                                                    </p>
                                                </div>
                                            )}
                                            {controlStats.highlights?.worst_person_by_rejects && (
                                                <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                                                    <p className="text-sm font-semibold text-red-800 mb-2">Pire par Rejets</p>
                                                    <p className="text-lg font-bold text-red-900">
                                                        {controlStats.highlights.worst_person_by_rejects.person_name}
                                                        <span className={`ml-2 px-1.5 py-0.5 text-xs rounded ${controlStats.highlights.worst_person_by_rejects.person_type === 'supervisor' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                                                            {controlStats.highlights.worst_person_by_rejects.person_type === 'supervisor' ? 'Prestataire' : 'Enquêteur'}
                                                        </span>
                                                    </p>
                                                    <p className="text-sm text-gray-600 mt-2">Ref: {controlStats.highlights.worst_person_by_rejects.person_ref}</p>
                                                    <p className="text-sm font-semibold text-red-800 mt-1">
                                                        Rejets: {controlStats.highlights.worst_person_by_rejects.rejections_count}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                                            <div className="bg-white rounded-lg p-4 shadow-sm">
                                                <p className="text-sm text-gray-600 mb-1">Total Postes</p>
                                                <p className="text-2xl font-bold text-gray-900">{controlStats.highlights?.total_postes || 0}</p>
                                            </div>
                                            <div className="bg-white rounded-lg p-4 shadow-sm">
                                                <p className="text-sm text-gray-600 mb-1">Total Soumissions</p>
                                                <p className="text-2xl font-bold text-gray-900">{controlStats.highlights?.total_submissions || 0}</p>
                                            </div>
                                            <div className="bg-white rounded-lg p-4 shadow-sm">
                                                <p className="text-sm text-gray-600 mb-1">Total Validées</p>
                                                <p className="text-2xl font-bold text-green-600">{controlStats.highlights?.total_validated || 0}</p>
                                            </div>
                                            <div className="bg-white rounded-lg p-4 shadow-sm">
                                                <p className="text-sm text-gray-600 mb-1">Total Rejetées</p>
                                                <p className="text-2xl font-bold text-red-600">{controlStats.highlights?.total_rejected || 0}</p>
                                            </div>
                                        </div>

                                        {/* Best Performing Poste Cards - CATI */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                            {controlStats.highlights?.best_poste_by_score && (
                                                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                                                    <p className="text-sm font-semibold text-green-800 mb-2">Meilleur Poste par Score</p>
                                                    <p className="text-lg font-bold text-green-900">Poste {controlStats.highlights.best_poste_by_score.poste_number}</p>
                                                    <p className="text-sm text-gray-600 mt-2">
                                                        {controlStats.highlights.best_poste_by_score.interviewer_name} ({controlStats.highlights.best_poste_by_score.interviewer_ref})
                                                    </p>
                                                    <p className="text-sm font-semibold text-green-800 mt-1">
                                                        Note Moyenne: {controlStats.highlights.best_poste_by_score.average_rating != null ? Number(controlStats.highlights.best_poste_by_score.average_rating).toFixed(2) + '%' : 'N/A'}
                                                    </p>
                                                </div>
                                            )}
                                            {controlStats.highlights?.best_poste_by_rejects && (
                                                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                                                    <p className="text-sm font-semibold text-green-800 mb-2">Meilleur Poste par Rejets</p>
                                                    <p className="text-lg font-bold text-green-900">Poste {controlStats.highlights.best_poste_by_rejects.poste_number}</p>
                                                    <p className="text-sm text-gray-600 mt-2">
                                                        {controlStats.highlights.best_poste_by_rejects.interviewer_name} ({controlStats.highlights.best_poste_by_rejects.interviewer_ref})
                                                    </p>
                                                    <p className="text-sm font-semibold text-green-800 mt-1">
                                                        Rejets: {controlStats.highlights.best_poste_by_rejects.rejections_count}
                                                    </p>
                                                </div>
                                            )}
                                        </div>

                                        {/* Worst Performing Poste Cards - CATI */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {controlStats.highlights?.worst_poste_by_score && (
                                                <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                                                    <p className="text-sm font-semibold text-red-800 mb-2">Pire Poste par Score</p>
                                                    <p className="text-lg font-bold text-red-900">Poste {controlStats.highlights.worst_poste_by_score.poste_number}</p>
                                                    <p className="text-sm text-gray-600 mt-2">
                                                        {controlStats.highlights.worst_poste_by_score.interviewer_name} ({controlStats.highlights.worst_poste_by_score.interviewer_ref})
                                                    </p>
                                                    <p className="text-sm font-semibold text-red-800 mt-1">
                                                        Note Moyenne: {controlStats.highlights.worst_poste_by_score.average_rating != null ? Number(controlStats.highlights.worst_poste_by_score.average_rating).toFixed(2) + '%' : 'N/A'}
                                                    </p>
                                                </div>
                                            )}
                                            {controlStats.highlights?.worst_poste_by_rejects && (
                                                <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                                                    <p className="text-sm font-semibold text-red-800 mb-2">Pire Poste par Rejets</p>
                                                    <p className="text-lg font-bold text-red-900">Poste {controlStats.highlights.worst_poste_by_rejects.poste_number}</p>
                                                    <p className="text-sm text-gray-600 mt-2">
                                                        {controlStats.highlights.worst_poste_by_rejects.interviewer_name} ({controlStats.highlights.worst_poste_by_rejects.interviewer_ref})
                                                    </p>
                                                    <p className="text-sm font-semibold text-red-800 mt-1">
                                                        Rejets: {controlStats.highlights.worst_poste_by_rejects.rejections_count}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Suppressions par Projet - Only show when "Toutes les enquêtes" is selected */}
                            {selectedSurveyForStats === null && controlStats.deletions_by_project && controlStats.deletions_by_project.length > 0 && (
                                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                                    <h4 className="text-xl font-bold text-gray-900 mb-4">Suppressions par Projet</h4>
                                    <div className="h-64">
                                        <Bar
                                            data={{
                                                labels: controlStats.deletions_by_project.map(p => p.project_name),
                                                datasets: [{
                                                    label: 'Suppressions',
                                                    data: controlStats.deletions_by_project.map(p => p.deletions_count),
                                                    backgroundColor: 'rgba(239, 68, 68, 0.8)',
                                                    borderColor: 'rgba(239, 68, 68, 1)',
                                                    borderWidth: 1
                                                }]
                                            }}
                                            options={{
                                                responsive: true,
                                                maintainAspectRatio: false,
                                                plugins: {
                                                    legend: {
                                                        display: false
                                                    }
                                                },
                                                scales: {
                                                    y: {
                                                        beginAtZero: true,
                                                        ticks: {
                                                            stepSize: 1
                                                        }
                                                    }
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Top 10 Interviewers / Supervisors */}
                            {controlStats.top_interviewers && (() => {
                                const renderPersonCard = (person, index, colorTheme) => {
                                    const bgColor = colorTheme === 'green' ? 'bg-green-100' : 'bg-red-100';
                                    const textColor = colorTheme === 'green' ? 'text-green-600' : 'text-red-600';
                                    const hoverBorder = colorTheme === 'green' ? 'hover:border-green-300' : 'hover:border-red-300';
                                    const reasonColor = colorTheme === 'green' ? 'text-green-700' : 'text-red-700';

                                    return (
                                        <div
                                            key={person.id + '_' + (person.person_type || 'int')}
                                            className={`bg-gray-50 rounded-lg border border-gray-200 ${hoverBorder} hover:shadow-md transition-all duration-200`}
                                        >
                                            <div className="p-3 sm:p-4">
                                                <div className="flex items-start gap-3 mb-3">
                                                    <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center">
                                                        <span className="text-lg sm:text-xl font-bold text-gray-400">{index + 1}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                                                        <div className={`flex-shrink-0 w-9 h-9 sm:w-11 sm:h-11 rounded-full ${bgColor} flex items-center justify-center`}>
                                                            <span className={`${textColor} font-bold text-xs sm:text-sm`}>
                                                                {person.name ? person.name.charAt(0).toUpperCase() : '?'}
                                                            </span>
                                                        </div>
                                                        <div className="min-w-0 flex-1">
                                                            <h3 className="text-sm sm:text-base font-semibold text-gray-900 truncate">
                                                                {person.name || 'N/A'}
                                                                {person.person_type === 'supervisor' && (
                                                                    <span className="ml-1 px-1.5 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">Prestataire</span>
                                                                )}
                                                            </h3>
                                                            <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                                                                <span className="text-xs text-gray-500">{person.ref || 'N/A'}</span>
                                                                {person.poste_number && (
                                                                    <span className="text-xs text-gray-500">- Poste {person.poste_number}</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center justify-between mb-3 pl-11 sm:pl-14">
                                                    <div className="flex items-center gap-2">
                                                        <div className="text-base sm:text-lg font-bold text-red-600">
                                                            {person.average_rating != null ? Number(person.average_rating).toFixed(1) + '%' : 'N/A'}
                                                        </div>
                                                        <div className="flex items-center">
                                                            {renderStarRating(person.average_rating, person.average_rating_raw, person.total_checks, person.most_common_reason)}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="pt-2 border-t border-gray-200 space-y-1.5 pl-11 sm:pl-14">
                                                    {person.most_common_reason && (
                                                        <div className="flex items-start gap-1.5">
                                                            <span className="text-xs text-gray-500 whitespace-nowrap">Raison:</span>
                                                            <span className={`text-xs font-semibold ${reasonColor} break-words`}>
                                                                {person.most_common_reason.label} ({person.most_common_reason.percentage}%)
                                                            </span>
                                                        </div>
                                                    )}
                                                    <div className="text-xs text-gray-500">
                                                        {person.total_checks || 0} enregistrement{person.total_checks !== 1 ? 's' : ''} controle{person.total_checks !== 1 ? 's' : ''}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                };

                                const renderTopSection = (title, icon, iconColor, data, colorTheme) => (
                                    <div className="bg-white rounded-xl p-4 sm:p-6 shadow-lg border border-gray-200">
                                        <h4 className={`text-xl sm:text-2xl font-bold ${iconColor} mb-4 sm:mb-6 flex items-center`}>
                                            <FontAwesomeIcon icon={icon} className="mr-2 sm:mr-3" />
                                            <span className="text-base sm:text-2xl">{title}</span>
                                        </h4>
                                        <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
                                            {data && data.length > 0 ? (
                                                data.map((person, index) => renderPersonCard(person, index, colorTheme))
                                            ) : (
                                                <div className="text-center py-12">
                                                    <p className="text-gray-500">Aucune donnee disponible</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );

                                return (
                                    <>
                                        {/* Top Interviewers */}
                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                                            {renderTopSection(
                                                showF2fView ? 'Top 10 Meilleurs Enqueteurs F2F' : 'Top 10 Meilleurs Scores',
                                                faTrophy, 'text-green-600',
                                                controlStats.top_interviewers.best, 'green'
                                            )}
                                            {renderTopSection(
                                                showF2fView ? 'Top 10 Pires Enqueteurs F2F' : 'Top 10 Pires Scores',
                                                faTimesCircle, 'text-red-600',
                                                controlStats.top_interviewers.worst, 'red'
                                            )}
                                        </div>

                                        {/* Top Supervisors - F2F only */}
                                        {showF2fView && controlStats.top_supervisors && (
                                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                                                {renderTopSection(
                                                    'Top 10 Meilleurs Prestataires',
                                                    faTrophy, 'text-green-600',
                                                    controlStats.top_supervisors.best, 'green'
                                                )}
                                                {renderTopSection(
                                                    'Top 10 Pires Prestataires',
                                                    faTimesCircle, 'text-red-600',
                                                    controlStats.top_supervisors.worst, 'red'
                                                )}
                                            </div>
                                        )}
                                    </>
                                );
                            })()}

                            {/* Répartition par source */}
                            {controlStats.source_breakdown && controlStats.source_breakdown.total > 0 && (
                                <div className="bg-gray-50 rounded-lg p-6">
                                    <h4 className="text-xl font-bold text-gray-900 mb-4">Répartition des Contrôles par Source</h4>
                                    <p className="text-xs text-gray-500 mb-4">Sur {controlStats.source_breakdown.total} contrôles validés ou rejetés.</p>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div className="bg-white rounded-lg p-4 shadow-sm border border-indigo-100">
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-sm font-semibold text-indigo-700">IA</span>
                                                <span className="px-2 py-0.5 rounded text-xs font-semibold bg-indigo-100 text-indigo-700">IA</span>
                                            </div>
                                            <div className="text-2xl font-bold text-gray-900">{controlStats.source_breakdown.ai.percent}%</div>
                                            <div className="text-xs text-gray-500">{controlStats.source_breakdown.ai.count} contrôles</div>
                                            <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                                                <div className="bg-indigo-500 h-2 rounded-full" style={{ width: `${controlStats.source_breakdown.ai.percent}%` }}></div>
                                            </div>
                                        </div>
                                        <div className="bg-white rounded-lg p-4 shadow-sm border border-blue-100">
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-sm font-semibold text-blue-700">Contrôleur</span>
                                            </div>
                                            <div className="text-2xl font-bold text-gray-900">{controlStats.source_breakdown.controller.percent}%</div>
                                            <div className="text-xs text-gray-500">{controlStats.source_breakdown.controller.count} contrôles</div>
                                            <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                                                <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${controlStats.source_breakdown.controller.percent}%` }}></div>
                                            </div>
                                        </div>
                                        <div className="bg-white rounded-lg p-4 shadow-sm border border-purple-100">
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-sm font-semibold text-purple-700">Resp. Contrôle</span>
                                            </div>
                                            <div className="text-2xl font-bold text-gray-900">{controlStats.source_breakdown.resp_controle.percent}%</div>
                                            <div className="text-xs text-gray-500">{controlStats.source_breakdown.resp_controle.count} contrôles</div>
                                            <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                                                <div className="bg-purple-500 h-2 rounded-full" style={{ width: `${controlStats.source_breakdown.resp_controle.percent}%` }}></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Distribution des Raisons par Projet */}
                            {controlStats.reasons_distribution && controlStats.reasons_distribution.length > 0 && (() => {
                                // Filter projects based on search term
                                const filteredProjects = controlStats.reasons_distribution.filter((project) => {
                                    if (!reasonsDistributionSearchTerm.trim()) {
                                        return true;
                                    }
                                    const searchLower = reasonsDistributionSearchTerm.toLowerCase();
                                    const projectName = (project.project_name || '').toLowerCase();
                                    const limesurveyId = (project.limesurvey_id || '').toLowerCase();
                                    return projectName.includes(searchLower) || limesurveyId.includes(searchLower);
                                });

                                return (
                                    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                                            <h4 className="text-xl font-bold text-gray-900">Distribution des Raisons par Projet</h4>
                                            <div className="flex items-center gap-2">
                                                <div className="relative flex-1 sm:flex-initial sm:w-64">
                                                    <FontAwesomeIcon 
                                                        icon={faSearch} 
                                                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                                                    />
                                                    <input
                                                        type="text"
                                                        placeholder="Rechercher par nom ou ID..."
                                                        value={reasonsDistributionSearchInput}
                                                        onChange={(e) => setReasonsDistributionSearchInput(e.target.value)}
                                                        onKeyPress={(e) => {
                                                            if (e.key === 'Enter') {
                                                                setReasonsDistributionSearchTerm(reasonsDistributionSearchInput);
                                                            }
                                                        }}
                                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-sm"
                                                    />
                                                </div>
                                                <button
                                                    onClick={() => setReasonsDistributionSearchTerm(reasonsDistributionSearchInput)}
                                                    className="h-10 w-10 px-0 py-0 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center"
                                                >
                                                    <FontAwesomeIcon icon={faSearch} />
                                                </button>
                                            </div>
                                        </div>
                                        {filteredProjects.length > 0 ? (
                                            <div 
                                                className={`space-y-6 ${filteredProjects.length > 3 ? 'max-h-[600px] overflow-y-auto pr-2' : ''}`}
                                            >
                                                {filteredProjects.map((project, index) => (
                                                    <div key={project.project_id} className="border border-gray-200 rounded-lg p-4 sm:p-5 md:p-6 shadow-sm hover:shadow-md transition-shadow">
                                                        <h5 className="font-semibold text-gray-900 mb-4 sm:mb-5 text-base sm:text-lg">{project.project_name}</h5>
                                                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
                                                            <div className="text-center p-2 sm:p-3 bg-gray-50 rounded-lg">
                                                                <p className="text-xs sm:text-sm text-gray-600 mb-2">Métier</p>
                                                                <p className="text-xl sm:text-2xl font-bold text-blue-600">{project.reasons.metier}%</p>
                                                            </div>
                                                            <div className="text-center p-2 sm:p-3 bg-gray-50 rounded-lg">
                                                                <p className="text-xs sm:text-sm text-gray-600 mb-2">Projet</p>
                                                                <p className="text-xl sm:text-2xl font-bold text-purple-600">{project.reasons.projet}%</p>
                                                            </div>
                                                            <div className="text-center p-2 sm:p-3 bg-gray-50 rounded-lg">
                                                                <p className="text-xs sm:text-sm text-gray-600 mb-2">Métier et Projet</p>
                                                                <p className="text-xl sm:text-2xl font-bold text-orange-600">{project.reasons.metier_et_projet}%</p>
                                                            </div>
                                                            <div className="text-center p-2 sm:p-3 bg-gray-50 rounded-lg">
                                                                <p className="text-xs sm:text-sm text-gray-600 mb-2">Rien</p>
                                                                <p className="text-xl sm:text-2xl font-bold text-gray-600">{project.reasons.rien}%</p>
                                                            </div>
                                                        </div>
                                                        <div className="mt-4 sm:mt-5 pt-3 sm:pt-4 border-t border-gray-100">
                                                            <p className="text-xs sm:text-sm text-gray-500">Total contrôles: <span className="font-semibold text-gray-700">{project.total_checks}</span></p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-center py-8 text-gray-500">
                                                Aucun projet trouvé pour "{reasonsDistributionSearchTerm}"
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}

                            {/* Résumé par Poste / Personne */}
                            {(controlStats.resume_data === undefined || (controlStats.resume_data && controlStats.resume_data.length > 0)) && (
                                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                                    <button
                                        onClick={() => {
                                            const next = !showResumeData;
                                            setShowResumeData(next);
                                            if (next) fetchResumeDataIfMissing();
                                        }}
                                        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                                    >
                                        <h4 className="text-xl font-bold text-gray-900">{showF2fView ? 'Resume par Personne' : 'Resume par Poste'}</h4>
                                        <FontAwesomeIcon 
                                            icon={showResumeData ? faChevronUp : faChevronDown} 
                                            className="text-gray-500 transition-transform duration-200"
                                        />
                                    </button>
                                    
                                    {showResumeData && resumeLoading && (
                                        <div className="px-6 py-8 border-t border-gray-100 text-center text-gray-500">
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto mb-2"></div>
                                            Chargement du résumé...
                                        </div>
                                    )}
                                    {showResumeData && !resumeLoading && (
                                        <div className="px-6 pb-6 border-t border-gray-100">
                                            {/* Search Input with Column Filter */}
                                            <div className="mt-4 mb-4 flex flex-col sm:flex-row gap-2">
                                                <select
                                                    value={resumeSearchColumn}
                                                    onChange={(e) => setResumeSearchColumn(e.target.value)}
                                                    className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-sm"
                                                >
                                                    <option value="all">Toutes les colonnes</option>
                                                    <option value="Projet">Questionnaire</option>
                                                    <option value="ID Projet">ID LimeSurvey</option>
                                                    {showF2fView ? (
                                                        <>
                                                            <option value="Type">Type</option>
                                                            <option value="Ref">Ref</option>
                                                            <option value="Nom">Nom</option>
                                                        </>
                                                    ) : (
                                                        <option value="Poste">Poste</option>
                                                    )}
                                                    <option value="Nombre_Enregistrements">Nb Enregistrements</option>
                                                    <option value="Note_Moyenne">Note Moyenne</option>
                                                    <option value="Nombre_Suppressions">Nb Suppressions</option>
                                                    <option value="Raison_Plus_Commune">Raison Plus Commune</option>
                                                    <option value="Premiere_Date_Controle">Premiere Date</option>
                                                    <option value="Derniere_Date_Controle">Derniere Date</option>
                                                </select>
                                                <input
                                                    type="text"
                                                    placeholder="Rechercher..."
                                                    value={resumeSearchTerm}
                                                    onChange={(e) => setResumeSearchTerm(e.target.value)}
                                                    onKeyPress={(e) => {
                                                        if (e.key === 'Enter') {
                                                            handleResumeSearch();
                                                        }
                                                    }}
                                                    className="flex-1 w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-sm"
                                                />
                                                <button
                                                    onClick={handleResumeSearch}
                                                    className="w-full sm:w-auto px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2 text-sm whitespace-nowrap"
                                                >
                                                    <FontAwesomeIcon icon={faSearch} />
                                                    <span>Rechercher</span>
                                                </button>
                                            </div>
                                            
                                            <div className="overflow-x-auto mt-4">
                                                <table className="min-w-full divide-y divide-gray-200">
                                                    <thead className="bg-red-600">
                                                        <tr>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Questionnaire</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">ID LimeSurvey</th>
                                                            {showF2fView ? (
                                                                <>
                                                                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Type</th>
                                                                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Ref</th>
                                                                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Nom</th>
                                                                </>
                                                            ) : (
                                                                <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Poste</th>
                                                            )}
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Nb Enregistrements</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Note Moyenne</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Nb Suppressions</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Metier (Nb)</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Projet (Nb)</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Les 2 (Nb)</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Rien (Nb)</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Metier (%)</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Projet (%)</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Les 2 (%)</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Rien (%)</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Raison Plus Commune</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Premiere Date</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Derniere Date</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="bg-white divide-y divide-gray-200">
                                                        {(resumeFilteredData || controlStats.resume_data || []).map((row, index) => (
                                                            <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-red-50'}>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row.Projet || 'N/A'}</td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row['ID Projet'] || 'N/A'}</td>
                                                                {showF2fView ? (
                                                                    <>
                                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                                                            {row.Type === 'Prestataire' ? (
                                                                                <span className="px-1.5 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">{row.Type}</span>
                                                                            ) : (
                                                                                <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">{row.Type || 'N/A'}</span>
                                                                            )}
                                                                        </td>
                                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row.Ref || 'N/A'}</td>
                                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row.Nom || 'N/A'}</td>
                                                                    </>
                                                                ) : (
                                                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row.Poste || 'N/A'}</td>
                                                                )}
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row.Nombre_Enregistrements || 0}</td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row.Note_Moyenne ? Number(row.Note_Moyenne).toFixed(2) : 'N/A'}</td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row.Nombre_Suppressions || 0}</td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row.Raison_Métier_Nombre || 0}</td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row.Raison_Projet_Nombre || 0}</td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row.Raison_Les2_Nombre || 0}</td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row.Raison_Rien_Nombre || 0}</td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row['Raison_Métier_Pourcentage'] ? Number(row['Raison_Métier_Pourcentage']).toFixed(2) + '%' : '0%'}</td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row['Raison_Projet_Pourcentage'] ? Number(row['Raison_Projet_Pourcentage']).toFixed(2) + '%' : '0%'}</td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row['Raison_Les2_Pourcentage'] ? Number(row['Raison_Les2_Pourcentage']).toFixed(2) + '%' : '0%'}</td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row['Raison_Rien_Pourcentage'] ? Number(row['Raison_Rien_Pourcentage']).toFixed(2) + '%' : '0%'}</td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row.Raison_Plus_Commune || 'N/A'}</td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row.Premiere_Date_Controle || 'N/A'}</td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row.Derniere_Date_Controle || 'N/A'}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Données Brutes */}
                            {(controlStats.raw_data === undefined || (controlStats.raw_data && controlStats.raw_data.length > 0)) && (
                                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                                    <button
                                        onClick={() => {
                                            const next = !showRawData;
                                            setShowRawData(next);
                                            if (next) fetchRawDataIfMissing();
                                        }}
                                        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                                    >
                                        <h4 className="text-xl font-bold text-gray-900">Données Brutes</h4>
                                        <FontAwesomeIcon 
                                            icon={showRawData ? faChevronUp : faChevronDown} 
                                            className="text-gray-500 transition-transform duration-200"
                                        />
                                    </button>
                                    
                                    {showRawData && rawLoading && (
                                        <div className="px-6 py-8 border-t border-gray-100 text-center text-gray-500">
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto mb-2"></div>
                                            Chargement des données brutes...
                                        </div>
                                    )}
                                    {showRawData && !rawLoading && (
                                        <div className="px-6 pb-6 border-t border-gray-100">
                                            {/* Search Input with Column Filter */}
                                            <div className="mt-4 mb-4 flex flex-col sm:flex-row gap-2">
                                                <select
                                                    value={rawDataSearchColumn}
                                                    onChange={(e) => setRawDataSearchColumn(e.target.value)}
                                                    className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-sm"
                                                >
                                                    <option value="all">Toutes les colonnes</option>
                                                    <option value="Projet">Questionnaire</option>
                                                    <option value="ID LimeSurvey">ID LimeSurvey</option>
                                                    {showF2fView ? (
                                                        <>
                                                            <option value="Type">Type</option>
                                                            <option value="Ref">Ref</option>
                                                            <option value="Nom">Nom</option>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <option value="Poste_Enqueteur">Poste</option>
                                                            <option value="Enquêteur">Enqueteur</option>
                                                            <option value="Réf. Enquêteur">Ref. Enqueteur</option>
                                                        </>
                                                    )}
                                                    <option value="Action">Action</option>
                                                    <option value="Note">Note</option>
                                                    <option value="Raison">Raison</option>
                                                    <option value="TEL">TEL</option>
                                                    <option value="Date de soumission">Date Soumission</option>
                                                    <option value="Date de controle">Date Controle</option>
                                                    <option value="Contrôleur">Controleur</option>
                                                    <option value="Réf. Contrôleur">Ref. Controleur</option>
                                                    <option value="ID de la réponse">ID Reponse</option>
                                                    <option value="Commentaire">Commentaire</option>
                                                    <option value="Raison de Rejet">Raison de Rejet</option>
                                                </select>
                                                <input
                                                    type="text"
                                                    placeholder="Rechercher..."
                                                    value={rawDataSearchTerm}
                                                    onChange={(e) => setRawDataSearchTerm(e.target.value)}
                                                    onKeyPress={(e) => {
                                                        if (e.key === 'Enter') {
                                                            handleRawDataSearch();
                                                        }
                                                    }}
                                                    className="flex-1 w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-sm"
                                                />
                                                <button
                                                    onClick={handleRawDataSearch}
                                                    className="w-full sm:w-auto px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2 text-sm whitespace-nowrap"
                                                >
                                                    <FontAwesomeIcon icon={faSearch} />
                                                    <span>Rechercher</span>
                                                </button>
                                            </div>
                                            
                                            <div className="overflow-x-auto mt-4">
                                                <table className="min-w-full divide-y divide-gray-200">
                                                    <thead className="bg-red-600">
                                                        <tr>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Questionnaire</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">ID LimeSurvey</th>
                                                            {showF2fView ? (
                                                                <>
                                                                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Type</th>
                                                                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Ref</th>
                                                                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Nom</th>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Poste</th>
                                                                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Enqueteur</th>
                                                                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Ref. Enqueteur</th>
                                                                </>
                                                            )}
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Action</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Note</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Raison</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">TEL</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Date Soumission</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Date Controle</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Controleur</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Ref. Controleur</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">ID Reponse</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Commentaire</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Raison de Rejet</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Lien LimeSurvey</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="bg-white divide-y divide-gray-200">
                                                        {(rawDataFilteredData || controlStats.raw_data || []).map((row, index) => (
                                                            <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-red-50'}>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row.Projet || 'N/A'}</td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row['ID LimeSurvey'] || 'N/A'}</td>
                                                                {showF2fView ? (
                                                                    <>
                                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                                                            {row.Type === 'Prestataire' ? (
                                                                                <span className="px-1.5 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">{row.Type}</span>
                                                                            ) : (
                                                                                <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">{row.Type || 'N/A'}</span>
                                                                            )}
                                                                        </td>
                                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row.Ref || 'N/A'}</td>
                                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row.Nom || 'N/A'}</td>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row.Poste_Enqueteur || 'N/A'}</td>
                                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row['Enquêteur'] || 'N/A'}</td>
                                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row['Réf. Enquêteur'] || 'N/A'}</td>
                                                                    </>
                                                                )}
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm">
                                                                    {row.Action === 'Validé' ? (
                                                                        <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800">{row.Action}</span>
                                                                    ) : row.Action === 'Rejeté' ? (
                                                                        <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800">{row.Action}</span>
                                                                    ) : row.Action === 'Mis en attente' ? (
                                                                        <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">{row.Action}</span>
                                                                    ) : (
                                                                        <span className="text-gray-900">{row.Action || 'N/A'}</span>
                                                                    )}
                                                                </td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row.Note || 'N/A'}</td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row.Raison || 'N/A'}</td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row.TEL || 'N/A'}</td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row['Date de soumission'] || 'N/A'}</td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row['Date de controle'] || 'N/A'}</td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row['Contrôleur'] || 'N/A'}</td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row['Réf. Contrôleur'] || 'N/A'}</td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row['ID de la réponse'] || 'N/A'}</td>
                                                                <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate" title={row.Commentaire || ''}>{row.Commentaire || ''}</td>
                                                                <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate" title={row['Raison de Rejet'] || ''}>{row['Raison de Rejet'] || ''}</td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm">
                                                                    {row['Lien LimeSurvey'] && row['Lien LimeSurvey'] !== 'N/A' ? (
                                                                        <a
                                                                            href={row['Lien LimeSurvey']}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="text-red-600 hover:text-red-800 underline"
                                                                        >
                                                                            Ouvrir
                                                                        </a>
                                                                    ) : (
                                                                        <span className="text-gray-400">N/A</span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="text-center py-12 text-gray-500">
                            Aucune donnée disponible
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ControlStats;

